import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { AuthUser, LoginPayload } from "@repo/types";
import { AuthApi, fetchCurrentUser, setAuthFailureHandler } from "@repo/api-client";
import { tokenStorage } from "@repo/lib/auth/token-storage";
import { useQueryClient } from "@tanstack/react-query";

type RegisterPayload = Parameters<typeof AuthApi.register>[0];

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

const publicRoutes = new Set([
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/tenant-portal",
]);

interface TransitionState {
  active: boolean;
  text: string;
  phase: "enter" | "exit";
}

const TRANSITION_HOLD_MS = 1500;
const TRANSITION_EXIT_MS = 450;

function AuthTransitionOverlay({
  state,
  onNavigate,
  onComplete,
}: {
  state: TransitionState;
  onNavigate: () => void;
  onComplete: () => void;
}) {
  const [phase, setPhase] = React.useState<"enter" | "exit">("enter");

  React.useEffect(() => {
    const timer = setTimeout(() => {
      onNavigate();
      setTimeout(() => setPhase("exit"), 150);
    }, TRANSITION_HOLD_MS);
    return () => clearTimeout(timer);
  }, [onNavigate]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        backgroundColor: "#fdf8f3",
        animation:
          phase === "enter"
            ? "authOverlayIn 300ms ease-out forwards"
            : `authFadeOut ${TRANSITION_EXIT_MS}ms ease-out forwards`,
      }}
      onAnimationEnd={() => {
        if (phase === "exit") onComplete();
      }}
    >
      <div className="flex flex-col items-center gap-3">
        <span
          className="font-bold text-brand-600"
          style={{
            fontSize: 26,
            animation: "authBlurReveal 600ms ease-out 150ms forwards, authBreath 2.5s ease-in-out 750ms infinite",
            opacity: 0,
          }}
        >
          mann.reservation
        </span>

        <div
          style={{
            width: 48,
            height: 1.5,
            backgroundColor: "rgba(178,98,102,0.3)",
            borderRadius: 1,
            animation: "authLineExpand 400ms ease-out 500ms forwards",
            opacity: 0,
            transformOrigin: "center",
          }}
        />

        <p
          className="font-semibold tracking-wide text-brand-500"
          style={{
            fontSize: 15,
            animation: "authTextReveal 400ms ease-out 650ms forwards",
            opacity: 0,
          }}
        >
          {state.text}
        </p>
      </div>
    </div>
  );
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const pathnameRef = React.useRef(pathname);
  pathnameRef.current = pathname;
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [transition, setTransition] = React.useState<TransitionState>({
    active: false,
    text: "",
    phase: "enter",
  });

  const navigateToDashboard = React.useCallback(() => {
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  const finishTransition = React.useCallback(() => {
    setTransition({ active: false, text: "", phase: "enter" });
  }, []);

  const hydrate = React.useCallback(async () => {
    // /m/ 是手機 App WebView 用的獨立 token-based 頁面（見下方 isPublic 判斷），
    // 自己管理 token 生命週期，不使用 AuthContext 的 user。這裡若照常執行，
    // 會跟頁面自己設定的 token 搶用同一個 tokenStorage 單例，
    // fetchCurrentUser 失敗時的 clearTokens() 還會把頁面剛設好的 token 清掉，
    // 導致該頁面之後的請求（訂閱狀態等）全部變成未授權。
    if (pathnameRef.current.startsWith("/m/")) {
      setLoading(false);
      return;
    }
    try {
      await tokenStorage.loadPersistedTokens();
      if (tokenStorage.getAccessToken()) {
        const profile = await fetchCurrentUser();
        setUser(profile);
        // 路由元件在 hydrate 完成前就可能已經掛載並發出請求（此時 token
        // 尚未從 storage 讀出），那些請求會拿到 401 並被 react-query 快取
        // 成錯誤，且沒有人會再自動重試，導致頁面停留在空/錯誤狀態直到手動
        // 整頁重新整理。hydrate 確認登入狀態後補一次 invalidate 讓它們重打。
        queryClient.invalidateQueries();
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("[AuthProvider] Failed to hydrate session", error);
      await tokenStorage.clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  React.useEffect(() => {
    hydrate().catch((error) => console.error(error));
  }, [hydrate]);

  React.useEffect(() => {
    setAuthFailureHandler(() => {
      setUser(null);
    });
    return () => setAuthFailureHandler(null);
  }, []);

  React.useEffect(() => {
    if (loading || transition.active) return;

    const currentPath = pathname ?? "";
    const isPublic =
      publicRoutes.has(currentPath) ||
      currentPath.startsWith("/tenant-portal/") ||
      currentPath.startsWith("/check-in/") ||
      currentPath.startsWith("/m/");
    const isPlatformAdminRoute = currentPath.startsWith("/platform-admin");

    if (!user && !isPublic && !isPlatformAdminRoute) {
      navigate("/login", { replace: true });
    }

    if (user && (currentPath === "/login" || currentPath === "/register")) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, navigate, pathname, transition.active, user]);

  const login = React.useCallback(async (payload: LoginPayload) => {
    setLoading(true);
    try {
      const { user: nextUser } = await AuthApi.login(payload);
      setUser(nextUser);
      setTransition({ active: true, text: "歡迎回來", phase: "enter" });
    } finally {
      setLoading(false);
    }
  }, []);

  const register = React.useCallback(async (payload: RegisterPayload) => {
    setLoading(true);
    try {
      const { user: nextUser } = await AuthApi.register(payload);
      setUser(nextUser);
      setTransition({ active: true, text: "歡迎加入", phase: "enter" });
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = React.useCallback(async () => {
    setLoading(true);
    try {
      await AuthApi.logout();
    } catch (error) {
      console.warn("[AuthProvider] logout", error);
    } finally {
      await tokenStorage.clearTokens();
      queryClient.clear();
      setUser(null);
      setLoading(false);
      navigate("/login", { replace: true });
    }
  }, [navigate, queryClient]);

  const refreshUser = React.useCallback(async () => {
    if (!tokenStorage.getAccessToken()) return;
    try {
      const profile = await fetchCurrentUser();
      setUser(profile);
    } catch (error) {
      console.error("[AuthProvider] refreshUser", error);
      await tokenStorage.clearTokens();
      setUser(null);
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      register,
      logout,
      refreshUser,
    }),
    [login, loading, logout, refreshUser, register, user]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {transition.active ? (
        <AuthTransitionOverlay
          state={transition}
          onNavigate={navigateToDashboard}
          onComplete={finishTransition}
        />
      ) : null}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
